// vendor libraries:
// @ts-ignore
import * as vec3 from 'gl-matrix/esm/vec3';

// our stuff:
import type { NumericalArray } from '../common/types';

export type AllowedHTMLTagNames = keyof HTMLElementTagNameMap;

/** @brief Simple function to create HTML element.
 *
 * @see {Document.createElement}
 * @template {AllowedHTMLTagNames} K
 * @param    {HTMLElement|null} parent Element to append newly created element to.
 * @param    {K} tagName Type of element to create (HTML tag).
 * @param    {string} [className] Element's className attribute.
 * @param    {string} [id] Element's id attribute.
 * @returns  {HTMLElementTagNameMap[K]} created element of type K.
 */
function createElement<K extends AllowedHTMLTagNames>(parent: HTMLElement | null, tagName: K, className?: string, id?: string): HTMLElementTagNameMap[K] {
    const el = document.createElement<K>(tagName);
    if (className) el.className = className;
    if (id) el.id = id;
    if (parent) {
        try {
            parent.appendChild(el);
        }
        catch (err) {
            alert('Failed to parent.appendChild: ' + err);
        }
    }
    return el;
}

/** @brief Simple function to create HTML <div> element.
 * 
 * Equivalent to createElement(parent, 'div', className, id)
 * @param    {HTMLElement} parent Element to append newly created element to.
 * @param    {string} [className] Element's className attribute.
 * @param    {string} [id] Element's id attribute.
 * @returns  {HTMLDivElement} created <div> element.
 */
function createDivElement(parent: HTMLElement | null, className?: string, id?: string): HTMLDivElement {
    const el = document.createElement('div');
    if (className) el.className = className;
    if (id) el.id = id;
    if (parent) parent.appendChild(el);
    return el;
}

type InputType =
    "button" | "checkbox" | "text" | "number" | "color" | "date" | "datetime-local" | "email" | "file" | "hidden" | "image" | "month" |
    "number" | "password" | "radio" | "range" | "reset" | "search" | "submit" | "tel" | "text" | "time" | "url" | "week";
/** @brief Simple function to create HTML <input> element.
 * @param    {HTMLElement} parent Element to append newly created element to.
 * @param    {string} [type] Type of input element to create (HTML tag).
 * @param    {string} [className] Element's className attribute.
 * @param    {string} [id] Element's id attribute.
 * @returns  {HTMLInputElement} created <input> element.
 */
function createInputElement(parent: HTMLElement | null, _type: InputType, className?: string, id?: string): HTMLInputElement {
    const el = document.createElement('input');
    el.type = _type;
    if (className) el.className = className;
    if (id) el.id = id;
    if (parent) parent.appendChild(el);
    return el;
}

/** @brief Simple function to create HTML <label> element.
 * @param    {HTMLElement} parent Element to append newly created element to.
 * @param    {string} [className] Element's className attribute.
 * @param    {string} [id] Element's id attribute.
 * @param    {string} [htmlFor] Element's for attribute.
 * @returns  {HTMLLabelElement} created <label> element.
 */
function createLabelElement(parent?: HTMLElement | null, className?: string, id?: string, htmlFor?: string): HTMLLabelElement {
    const el = document.createElement('label');
    if (className) el.className = className;
    if (id) el.id = id;
    if (htmlFor) el.htmlFor = htmlFor;
    if (parent) parent.appendChild(el);
    return el;
}

/** @brief Simple function to create HTML <select> element.
 * 
 * Equivalent to createElement(parent, 'select', className, id)
 * @param    {HTMLElement} parent Element to append newly created element to.
 * @param    {string} [className] Element's className attribute.
 * @param    {string} [id] Element's id attribute.
 * @returns  {HTMLSelectElement} created <select> element.
 */
function createSelectElement(parent?: HTMLElement | null, className?: string, id?: string): HTMLSelectElement {
    const el = document.createElement('select');
    if (className) el.className = className;
    if (id) el.id = id;
    if (parent) parent.appendChild(el);
    return el;
}

/** @brief Simple function to create HTML <option> element.
 * 
 * Equivalent to createElement(parent, 'option', className, id)
 * @param    {HTMLElement} parent Element to append newly created element to.
 * @param    {string} [className] Element's className attribute.
 * @param    {string} [id] Element's id attribute.
 * @returns  {HTMLOptionElement} created <option> element.
 */ 
function createOptionElement(parent?: HTMLElement | null, className?: string, id?: string): HTMLOptionElement {
    const el = document.createElement('option');
    if (className) el.className = className;
    if (id) el.id = id;
    if (parent) parent.appendChild(el);
    return el;
}

/** @brief Simple function to create HTML <canvas> element.
 * 
 * Equivalent to createElement(parent, 'canvas', className, id)
 * @param    {HTMLElement} [parent] Element to append newly created element to.
 * @param    {string} [className] Element's className attribute.
 * @param    {string} [id] Element's id attribute.
 * @returns  {HTMLCanvasElement} created <canvas> element.
 */
function createCanvasElement(parent?: HTMLElement | null, className?: string, id?: string): HTMLCanvasElement {
    const el = document.createElement("canvas");
    if (className) el.className = className;
    if (id) el.id = id;
    if (parent) parent.appendChild(el);
    return el;
}




/** @brief Simple function to create HTML <input> element with type "checkbox". 
 * Some customization added: label, color, click callback ... 
 *
 * @param {HTMLElement} parent
 * @param {string} [name]
 * @param {boolean} [checked]
 * @param {string} [classname]
 * @param {string} [id]
 * @param {string} [type="checkbox"]
 * @param {(ev: MouseEvent) => void} [callback]
 * @returns
 */
function createCheckBox(parent: HTMLElement | null, name?: string, checked?: boolean, classname?: string, id?: string, type: string = "checkbox", callback?: (ev: MouseEvent) => void) {
    const check = createElement(parent, "input", classname, id);
    check.type = type;
    if (checked)
        check.setAttribute("checked", "true");

    parent.appendChild(document.createTextNode(" "));

    if (name) {
        const label = createLabelElement(parent);
        label.innerHTML = name;
        label.style.color = "#3f4040";
        label.setAttribute("for", id);

        label.style.marginBottom = ".1rem"; // fix roboot.min.css!
    }

    if (typeof callback === 'function') {
        check.onclick = function (ev) {
            ev.stopPropagation(); // the click event won't be propagated to parent elements
            return callback(ev);
        };
    }

    return check;
}




/**
 * @param {string} idText
 * @param {boolean} defValue
 * @returns {boolean}
 */
function getCheckValue(idText: string, defValue: boolean): boolean { // As String!
    if (!idText) console.error("getCheckValue =", idText)
    const text = document.getElementById(idText)
    if (text && text instanceof HTMLInputElement) {
        return text.checked ? true : false;
    }
    return defValue;
}

/**
 * @param {HTMLElement} parent
 * @param {string} name
 * @param {string} value
 * @param {string} id
 * @returns
 */
function createInput(parent: HTMLElement, name: string, value: string, classname?: string, id?: string): HTMLInputElement {
    const label = createLabelElement(parent);
    label.innerHTML = name;
    //label.style.color = "#3f4040";
    //label.style.margin = "0px"; // remove  "margin-bottom: .5rem;"
    label.style.marginBottom = ".1rem"; // fix roboot.min.css!
    label.style.width = "14rem"
    label.setAttribute("for", id);

    parent.appendChild(document.createTextNode(" "));

    const text = createElement(parent, "input", classname, id);
    text.type = "text";
    text.value = value;
    //text.style.width = "4rem"

    return text;
}

/**
 * @param {string} idText
 * @param {string} defValue
 * @returns {string}
 */
function getInputValue(idText: string, defValue: string): string {
    if (!idText) console.error("getInputValue =", idText)
    const text = document.getElementById(idText)
    if (text && text instanceof HTMLInputElement)
        return text.value;

    return defValue;
}

/**
 * @param {string} idText
 * @param {number} defValue
 * @returns {number}
 */
function getInputIntValue(idText: string, defValue: number): number {
    if (!idText) console.error("getInputIntValue =", idText)
    const text = document.getElementById(idText)
    if (text && text instanceof HTMLInputElement)
        return Number.parseInt(text.value, 10);

    return defValue;
}

/**
 * @param {string} idText
 * @param {number} defValue
 * @returns {number}
 */
function getInputFloatValue(idText: string, defValue: number): number {
    if (!idText) console.error("getInputFloatValue =", idText)
    const text = document.getElementById(idText)
    if (text && text instanceof HTMLInputElement)
        return Number.parseFloat(text.value); //?? Use valueAsNumber for "number" type input

    return defValue;
}

/**
 * @param {string} idText
 * @param {string | number} value
 */
function setInputValue(idText: string, value: string | number): void {
    if (!idText) console.error("setInputValue =", idText)
    const text = document.getElementById(idText)
    if (text && text instanceof HTMLInputElement) {
        text.value = (typeof value === 'string') ? value : value.toString();
    }
}

/** @brief Simple function to create HTML text.
 * @param    {HTMLElement} parent Element to append newly created text to.
 * @param    {string}      text Text to be contained in text node.
 * @returns  {HTMLElement}
 */
function createTextNode(parent: HTMLElement, text: string): Text {
    const el = document.createTextNode(text);
    if (parent) parent.appendChild(el);
    return el;
}

/** @brief Returns element's attribute.
 * @param    {Element} el Element to get attribute from.
 * @param    {string} attrName Attribute to get.
 * @returns  {string | undefined}
 */
function getAttributeValue(el: Element, attrName: string): string | undefined {
    try {
        // @ts-ignore
        return String(el.attributes[attrName]/*.getNamedItem(attrName)*/.nodeValue);
    }
    catch (e) {
        return undefined;
    }
}

/** @brief Returns element's attribute.
 * @param    {Element} el Element to get attribute from.
 * @param    {string} attrName Attribute to get.
 * @param?    {number|undefined} defvalue default value if attribute is not found or bad.
 * @returns  {number|undefined}
 */
function getAttrValueInt(el: Element, attrName: string, defvalue?: number): number | undefined {
    try {
        // @ts-ignore
        const val = Number.parseInt(el.attributes[attrName].nodeValue, 10);
        if (!Number.isNaN(val))
            return val;
    }
    catch (e) {;}
    return defvalue;
}

/** @brief Returns element's attribute.
 * @param    {Element} el Element to get attribute from.
 * @param    {string} attrName Attribute to get.
 * @param?    {number|undefined} defvalue default value if attribute is not found or bad.
 * @returns  {boolean|undefined}
 */
function getAttrValueBool(el: Element, attrName: string, defvalue?: boolean): boolean | undefined {
    try {
        // @ts-ignore
        const attrvalue = el.attributes[attrName].nodeValue;
        if (attrvalue === "true") return true;
        if (attrvalue === "false") return false;
        const val = Number.parseInt(attrvalue, 10);
        if (!Number.isNaN(val))
            return (val !== 0) ? true : false;
    }
    catch (e) {;}
    return defvalue;
}

/** @brief Returns element's attribute.
 * @param    {Element} el Element to get attribute from.
 * @param    {string} attrName Attribute to get.
 * @param?    {number|undefined} defvalue default value if attribute is not found or bad.
 * @returns  {number|undefined}
 */
function getAttrValueFloat(el: Element, attrName: string, defvalue?: number): number | undefined {
    try {
        // @ts-ignore
        const val = Number.parseFloat(el.attributes[attrName].nodeValue);
        if (!Number.isNaN(val))
            return val;
    }
    catch (e) {;}
    return defvalue;
}

/** @brief Returns tag
 * @param    {Element} parent element of Tag to search.
 * @param    {string} tagName Tag to search.
 * @returns  {Element}
 */
function findTagByName(parent: HTMLElement, tagName: string): Element {
    let el = parent.firstElementChild
    for (; el; el = el.nextElementSibling) {
        if (el.tagName == tagName)
            break;
    }
    return el;
}

/** @brief Returns element's attribute.
 * @param    {Element} parent element of Tag to search.
 * @param    {string} tagName Tag to search.
 * @param    {string} attrName Attribute to search.
 * @param    {string} value of attribute to search.
 * @returns  {Element}
 */
function findTagWithAttr(parent: HTMLElement, tagName: string, attrName: string, value: string): Element {
    //TODO: if tagName!==undefined and value!==undefined:    parent.querySelector(`${tagName}[${attrName}^="${value}"]`);
    let el = parent.firstElementChild
    for (; el; el = el.nextElementSibling) {
        if (tagName !== undefined && el.tagName != tagName)
            continue;
        let attr;
        // @ts-ignore
        try { attr = el.attributes[attrName] }
        catch (e) { continue; }
        if (value === undefined || attr.nodeValue == value)
            break;
    }
    return el;
}

/** @brief Create tooltip with a text
 * @param    {HTMLElement} target Element to get attribute from.
 * @param    {string} attrName text.
 * @returns  {HTMLElement}
 */
function addTooltip(target: HTMLElement, text: string): HTMLElement {
    let timershow: ReturnType<typeof setTimeout>;
    let timerhide: ReturnType<typeof setTimeout>;

  
        const parent = document.body;
        const tooltp = createElement(parent/*target*/, "div", "customTooltip hidden", "tooltipItem");
        tooltp.onmouseover = function() {
            clearTimeout(timerhide);
            timerhide = undefined;
        }
        tooltp.onmouseout = function() {
            hideTooltip();
        }
        tooltp.onclick = function() {
            hideTooltip();
        }
  

    function showTooltip() {
        clearTimeout(timershow);
        timershow = undefined;

        tooltp.classList.remove("hidden");
        tooltp.innerHTML = text;

        const elemRect = target.getBoundingClientRect();

        const tooltip_top = (elemRect.bottom + 6);
        const tooltip_left = elemRect.left;

        const tooltip_rect = tooltp.getBoundingClientRect();

        const tooltip_right = tooltip_left + (tooltip_rect.width + 25/*for shadow*/);
        const tooltip_bottom = tooltip_top + (tooltip_rect.height + 28/*for shadow*/);

        const wind_right = document.documentElement.clientWidth || window.innerWidth;

        const offset_width = wind_right - tooltip_right;

        if (offset_width < 0)
            tooltp.style.left = tooltip_left + offset_width + "px"; // .style need 'px' at the end
        else
            tooltp.style.left = tooltip_left + "px";

        const wind_bottom = document.documentElement.clientHeight || window.innerHeight;

        const offset_height = wind_bottom - tooltip_bottom;

        if (offset_height < 0)
            tooltp.style.top = tooltip_top + offset_height + "px";
        else
            tooltp.style.top = tooltip_top + "px";
    }

    function hideTooltip() {
        clearTimeout(timerhide);
        timerhide = undefined;

        tooltp.classList.add("hidden");
    }

    if (target) {
        if (text) {
            target.onmouseover = function() {
                clearTimeout(timerhide);
                timerhide = undefined;

                clearTimeout(timershow);
                timershow = undefined;

                timershow = setTimeout(showTooltip, 1000);
            }
            target.onmouseout = function() {
                clearTimeout(timershow);
                timershow = undefined;

                clearTimeout(timerhide);
                timerhide = undefined;

                timerhide = setTimeout(hideTooltip, 10);
            }
        }
    }
    return tooltp;
}

/** @brief Parses input string as XML document.
 * @param    {string} response String to parse.
 * @returns  {XMLDocument} Parsed document.
 */
function getXmlObj(response: string): XMLDocument|undefined {
    const parser = new DOMParser();
    let xmlDocObj: XMLDocument|undefined=undefined;
    try {
        xmlDocObj = parser.parseFromString(response, "text/xml");
    } catch (e) {
        const errorMessage = e instanceof Error ? e.message : 'unknown error';

        alert(`${errorMessage} (parsing recieved XML)`);
    }

    return xmlDocObj;
}

/** @brief Parses input string as XML document and return root node.
 * @param    {string} response String to parse.
 * @returns  {HTMLElement} Root node of the parsed document.
 */
function getXmlObjRoot(response: string): HTMLElement|undefined {
    const xmlDocObj = getXmlObj(response);
    if (xmlDocObj)
        return xmlDocObj.documentElement;//firstElementChild
    return undefined;
}

/** @brief Returns XML Element converted to string.
 *
 * Exists to fix outerHTML not being implemented in IE11.
 * @param {Element} node
 * @link https://stackoverflow.com/a/45410596
 */
function getOuterHTML(node: Element) {
    return node.outerHTML || new XMLSerializer().serializeToString(node);
}

/** @brief Parses input number as decimal containing RGB values and returns them as array.
 * @param	{number} num Decimal representation of some RGB color.
 * @returns {number[]} Array with component values: [r, g, b].
 */
function getRGBArray(num: number): number[] {
    const r = (num      ) & 0xff;
    const g = (num >>  8) & 0xff;
    const b = (num >> 16) & 0xff;
    return [r, g, b];
}

/** @brief Parses input number as decimal containing RGBA values and returns them as array of components in range [0; 1.0].
 * @param	{number} num Decimal representation of some RGB color.
 * @returns {Float32Array} Array with component values (0,1) : [r, g, b].
 */
function getRGBAArray(num: number): Float32Array {
    const r = (num      ) & 0xff;
    const g = (num >>  8) & 0xff;
    const b = (num >> 16) & 0xff;
    const alpha = (num >> 24) & 0xff;
    return new Float32Array([r / 0xff, g / 0xff, b / 0xff, alpha / 0xff]);
}

/** @brief Parses input number as decimal containing RGB values and returns them as CSS string.
 * @param	{number} num Decimal representation of some RGB color.
 * @returns {string} CSS representation of passed color in format "rgb(r, g, b)".
 */
function getRGB(num: number): string {
    const r = (num      ) & 0xff;
    const g = (num >>  8) & 0xff;
    const b = (num >> 16) & 0xff;
    return `rgb(${r}, ${g}, ${b})`;
}

/** @brief Combines passed RGB components into single decimal value.
 * @param	{number} r Red component.
 * @param	{number} g Green component.
 * @param	{number} b Blue component.
 * @returns {number} Decimal representing passed RGB components.
 */
function getRGBInteger(r: number, g: number, b: number): number {
    return ((r & 0xff)) + ((g & 0xff) << 8) + ((b & 0xff) << 16);
}

/** @brief Combines passed RGB components into single decimal value.
 * @param	{number} r Red component.
 * @param	{number} g Green component.
 * @param	{number} b Blue component.
 * @returns {number} Decimal representing passed RGB components.
 */
function RGB(r: number, g: number, b: number): number { // WINAPI name
    return ((r & 0xff)) + ((g & 0xff) << 8) + ((b & 0xff) << 16);
}

/** @brief Returns a mix of two colors, passed as decimals. Resulting color is this: _ratio_ percent of _color1_ and _(1.0 - ratio)_ percent of _color2_.
 * @param {number} color1 First color to mix (as decimal).
 * @param {number} color2 Second color to mix (as decimal).
 * @param {number} [ratio=0.5] This ratio is used to mix colors. 0.5 by default (colors are mixed equally).
 * @returns Mixed color between two passed colors, as decimal.
 */
function mixDecimalColors(color1: number, color2: number, ratio: number = 0.5): number {
    if (ratio < 0.0) ratio = 0.0;
    if (ratio > 1.0) ratio = 1.0;

    const ratio1 = ratio;
    const ratio2 = 1.0 - ratio;

    const [r1, g1, b1] = getRGBArray(color1);
    const [r2, g2, b2] = getRGBArray(color2);

    return getRGBInteger(ratio1 * r1 + ratio2 * r2, ratio1 * g1 + ratio2 * g2, ratio1 * b1 + ratio2 * b2);
}

/** @brief Sets CSS transform rule for element. If only one parameter is passed then transformation is reset.
 *
 * Currently only supports 2D translation.
 *
 * Translation is set in CSS size string format, i.e. "10px", "5%", etc.
 * @param    {HTMLElement} elem Element to apply translation to.
 * @param    {string} [translatex] Translate element by X.
 * @param    {string} [translatey] Translate element by Y.
 */
function setElementTransform(elem?: HTMLElement, translatex?: string, translatey?: string): void {
    if (!elem)
        return;
    let transform_string = '';

    // only change transform from reset to something if we have both translatex and translatey set
    if (translatex !== undefined && translatey !== undefined) {
        transform_string = 'translate(' + translatex + ', ' + translatey + ')';
    }

    if (elem.style.transform !== undefined) {
        elem.style.transform = transform_string;
    }
    else if (elem.style.webkitTransform !== undefined) {
        elem.style.webkitTransform = transform_string;
    }
}

/** @brief Returns message from trown error (or string). If given object is not a valid Error object, returns "unknown error" string.
 *
 * @param {unknown} error Object thrown from somewhere (supports Error objects and strings).
 * @param {string} [fallbackErrorMessage='unknown error'] Fallback error message (if message cannot be found in the first argument)
 * @returns {string} Error message or fallback error message.
 */
function getErrorMessage(error: unknown, fallbackErrorMessage: string = 'unknown error'): string {
    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === 'string') {
        return error;
    }

    return fallbackErrorMessage;
}



class Point2d {
    x: number;
    y: number;
    constructor(x: number, y: number) {
        this.x = x;
        this.y = y;
    }
}

function pointInFrameRect(p: Point2d, min: Point2d, max: Point2d): number {
    if (min && max)
        if (p.x < min.x || p.x > max.x || p.y < min.y || p.y > max.y)
            return 0; // outside
    return 1;
}

function IsBetweenByX(a: Point2d, b: Point2d, p: Point2d): boolean {
    return (a.x < p.x) !== (b.x < p.x);
}

function RightCrossing(a: Point2d, p: Point2d, b: Point2d): boolean {
    const a1 = (a.y - p.y) * (b.x - p.x);
    const a2 = (b.y - p.y) * (a.x - p.x);

    //return (a1 > a2)===(b.x > a.x);
    return (a1 < a2) === (b.x > a.x); // Right/Left coordinate system?
}

function CalcLineCross(p: Point2d, a: Point2d, b: Point2d, crossNum: number): number {
    if (b.x === p.x) {
        if (b.y === p.y)
            return 2; // vertex code
        else
            if ((a.x === p.x) && (a.y > p.y))
                if (b.y < p.y)
                    return 3; // edge code
    }

    if (IsBetweenByX(a, b, p)) {
        if (a.y >= p.y) {
            if (b.y > p.y)
                crossNum = 1 - crossNum;
            else
                if (RightCrossing(a, b, p))
                    crossNum = 1 - crossNum;
        }
        else {
            if (b.y > p.y)
                if (RightCrossing(a, b, p))
                    crossNum = 1 - crossNum;
        }
    }
    return crossNum;
}


/* K.Hormann
*   Algorithm of detecting point in polygon
*
*	see https://www.inf.usi.ch/hormann/papers/Hormann.2001.TPI.pdf
*/
function PtInPolygon(p: Point2d, polygon: Point2d[], num: number): number {
    if (num <= 1)
        return 0;// out of polygon

    let pb = polygon[0];
    if ((pb.x === p.x) && (pb.y === p.y))
        return 2; // vertex code

    let crossNum = 0;
    for (let i = 1; i < num; i++) {
        let pa = pb;
        pb = polygon[i];

        crossNum = CalcLineCross(p, pa, pb, crossNum);
        if (crossNum > 1)		// 2 or 3
            return crossNum;	// vertex or edge
    }

    return crossNum;
}

function pointInPolygon(p: Point2d, polygon: Point2d[], num: number, min: Point2d, max: Point2d): number {

    if (min !== undefined) {
        if (!pointInFrameRect(p, min, max))
            return 0; // outside
    }

    const n = PtInPolygon(p, polygon, num);
    return n;
}

const DEG_TO_RAD = 0.0174532925199433;

function normalizeBy360(angle: number): number {
    return (angle > 360.0) ? angle - (angle / 360.0) * 360 : angle;
}

function normalizeAngle180(angle_deg: number) {
    if (angle_deg < -180)
        return (angle_deg % 360) + 360;
    if (angle_deg > 180)
        return (angle_deg % 360) - 360;
    return angle_deg;
}

export function calcVectorsAngle(vec0: NumericalArray, vec1: NumericalArray): number {
    const a = [vec0[0], vec0[1], vec0[2]];
    vec3.normalize(a, a);

    const b = [vec1[0], vec1[1], vec1[2]];
    vec3.normalize(b, b);

    let cos_alpha = vec3.dot(a, b);
    if (cos_alpha > 1)
        cos_alpha = 1;
    else if (cos_alpha < -1)
        cos_alpha = -1;
    let alpha = Math.acos(cos_alpha);

    if (a[0]*b[1] - a[1]*b[0] < 0)
        alpha *= -1;

    return alpha;
}
function _setTheme(mode: boolean): void {
    const oldtheme = mode ? "LightTheme" : "DarkTheme"
    const newtheme = mode ? "DarkTheme" : "LightTheme"
    const workspace = document.body;/*document.body is messageBox parent*/ //document.getElementById('workspace_root');
    if (workspace) {
        workspace.classList.remove(oldtheme)
        workspace.classList.add(newtheme)
    }

    /*
    const theme=mode?"-dark":"-light"
    for (const styleSheet of document.styleSheets) {
        const rules = styleSheet.cssRules || styleSheet.rules;
        for(const selector of selectors) {
            const ruleTheme=findRule(rules, selector+theme);
            if(ruleTheme) {
                // Copy content of the themed rule
                copyRule(styleSheet, rules, selector, ruleTheme);
            }
        }
    }
    */
}

function isCustomEvent(event: Event): event is CustomEvent {
    return event instanceof CustomEvent;
}

function round_float(n: number, digits: number = 0): number {
    const multiplicator = Math.pow(10, digits);
    n = Number.parseFloat((n * multiplicator).toFixed(11));
    return Math.round(n) / multiplicator;
}


function format(format: string, ...args: string[]): string { // python-like String format function
    // Example:
    // format("My name is {0}, I'm {1}", name, role)
    // >       My name is Vasya, I'm Software Developer
    return format.replace(/{(\d+)}/g, function (match:string, number:number) {
        return args[number] !== undefined ? args[number] : match;
    });
};

export function formatMinValue(v: number | undefined, acc: number): string | undefined {
  if (v === undefined) return undefined;
  const f = Math.pow(10, acc);
  return (Math.floor(v * f) / f).toString(); // round down
}
export function formatMaxValue(v: number | undefined, acc : number): string | undefined {
  if (v === undefined) return undefined;
  const f = Math.pow(10, acc);
  return (Math.ceil(v * f) / f).toString(); // round up
}

export {
    createElement,
    createDivElement,
    createInputElement,
    createLabelElement,
    createCanvasElement,
    createSelectElement,
    createOptionElement,
    createTextNode,
    getAttributeValue,
    getAttrValueInt,
    getAttrValueBool,
    getAttrValueFloat,
    getOuterHTML,
    getErrorMessage,
    findTagByName,
    findTagWithAttr,
    addTooltip,
    getXmlObj,
    getXmlObjRoot,
    getRGB,
    getRGBArray,
    getRGBAArray,
    getRGBInteger,
    RGB,
    mixDecimalColors,
    setElementTransform,
    pointInFrameRect,
    IsBetweenByX,
    RightCrossing,
    PtInPolygon,
    pointInPolygon,
    DEG_TO_RAD,
    normalizeBy360,
    normalizeAngle180,
    _setTheme,
    createCheckBox,
    getCheckValue,
    createInput,
    getInputValue,
    getInputIntValue,
    getInputFloatValue,
    setInputValue,
    Point2d,
    isCustomEvent,
    round_float,
    format,
};

